<template>
  <SoDialog
    v-model="value"
    :loading="loading"
    card-title="Under Review Periods"
    data-testid="under-review-periods-modal"
    :max-width="1200"
  >
    <SoErrorBoundary>
      <SoDataTable
        v-if="nonActiveReviewPeriodDataTableItems.length"
        :headers="headers"
        :items="nonActiveReviewPeriodDataTableItems"
        :has-pagination="false"
        :searchable="false"
        show-copy-to-clipboard
        data-testid="non-active-review-periods-table"
        class="mb-8"
        row-clickable
        @click:row="openReviewPeriod"
      />

      <h5 class="mb-4">Active</h5>
      <SoDataTable
        v-if="activeReviewPeriodDataTableItems.length"
        :headers="headers"
        :items="activeReviewPeriodDataTableItems"
        :has-pagination="false"
        :searchable="false"
        show-copy-to-clipboard
        data-testid="active-review-periods-table"
        row-clickable
        @click:row="openReviewPeriod"
      >
        <template #itemActions="{ internalItem }">
          <div class="d-flex gap-2 justify-end">
            <SoButton
              title="Edit"
              prepend-icon="pencil"
              type="table-action"
              data-testid="active-review-periods-table-edit-btn"
              @click.stop="showEditReviewPeriodForm(internalItem.raw)"
            />
            <SoButton
              title="Stop"
              type="table-action"
              data-testid="active-review-periods-table-stop-btn"
            />
          </div>
        </template>
      </SoDataTable>
      <SoStatus
        v-else
        :status="new Status('default', 'No Active Review Periods')"
        data-testid="no-active-review-periods"
      />
      <SoForm
        ref="form"
        data-testid="under-review-periods-form"
        @submit="handleSubmit()"
      >
        <v-expand-transition>
          <div v-show="showReviewPeriodForm">
            <SoDivider class="my-8" />
            <h5 class="mb-4">
              {{ isEditForm ? "Edit" : "Add" }} Review Period
            </h5>
            <SoAlert
              v-if="isEditForm"
              type="info"
              title="Editing a review will carry over the details of the existing review to a new one. The original
              review will appear as closed in the section above."
              data-testid="under-review-periods-create-note-alert"
              class="mb-4"
            />
            <div class="d-flex mb-8">
              <SoSelect
                v-model="form.reason"
                :items="reviewPeriodReasons"
                :rules="reasonRules"
                class="w-50 mr-5"
                label="Reason*"
                placeholder="Choose"
                data-testid="under-review-periods-reason-select"
              />

              <SoDatePicker
                v-model="form.from"
                label="From*"
                :rules="[rules.required]"
                class="mr-5"
                data-testid="under-review-periods-from-date"
              />

              <SoDatePicker
                v-model="form.to"
                label="To*"
                :rules="toDateRules"
                data-testid="under-review-periods-to-date"
              />
            </div>

            <h6>Suppressed</h6>
            <div class="d-flex">
              <SoCheckbox
                v-model="form.isDunningSuppressed"
                label="Dunning"
                class="mr-12"
              />
              <SoCheckbox
                v-model="form.isBillingSuppressed"
                label="Billing"
                class="mr-12"
              />
              <SoCheckbox
                v-model="form.isPaymentCollectionSuppressed"
                label="Payment collection"
              />
            </div>
            <SoTextarea
              v-model="form.description"
              label="Additional notes"
              class="mt-8"
            />
          </div>
        </v-expand-transition>
        <template #actions="{ reset }">
          <div class="d-flex mt-8">
            <v-spacer />
            <SoButton
              v-if="showReviewPeriodForm"
              title="Cancel"
              large
              @click="(reset(), (showReviewPeriodForm = false))"
            />
            <SoButton
              v-if="showReviewPeriodForm && !isEditForm"
              :loading="addReviewPeriodMutator.isPending"
              title="Create Review"
              type="success"
              class="ml-2"
              data-testid="under-review-periods-add-btn"
              large
              submit
            />
            <SoButton
              v-else-if="showReviewPeriodForm && isEditForm"
              :loading="editReviewPeriodMutator.isPending"
              title="Edit Review"
              type="success"
              class="ml-2"
              data-testid="under-review-periods-edit-btn"
              large
              submit
              :disabled="!editFormChanged"
            />
            <SoButton
              v-else
              :loading="addReviewPeriodMutator.isPending"
              title="Create Review"
              type="success"
              class="ml-2"
              data-testid="under-review-periods-add-btn"
              large
              @click="showReviewPeriodForm = true"
            />
          </div>
        </template>
      </SoForm>
      <SoAlert
        v-if="submitError"
        type="error"
        title="Failed to create review period"
        class="mt-4"
        data-testid="under-review-periods-create-alert"
      >
        {{ submitError }}
      </SoAlert>

      <SoAlert
        v-if="editReviewPeriodMutator.isError"
        type="error"
        title="Failed to edit review period"
        class="mt-4"
        data-testid="under-review-periods-edit-alert"
      />

      <SoAlert
        v-if="addNoteMutator.isError"
        type="error"
        title="Edited review period but failed to create an account note"
        class="mt-4"
        data-testid="under-review-periods-create-note-alert"
      />
    </SoErrorBoundary>
  </SoDialog>

  <UnderReviewPeriodModal
    v-model="openReviewPeriodModal"
    :review-period-id="currentReviewPeriodId"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    SoDataTableColumnType,
    SoDataTableCustomComponent,
  } from "@/types/components/SoDataTable"
  import { formatDateTime } from "@/helpers/dateHelper"
  import { errorReporter } from "@/services/ErrorService"
  import { reviewPeriodReasons } from "@/constants/reviewPeriodReasons"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDivider from "@/components/SoDivider.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoTextarea from "@/components/formControls/SoTextarea.vue"
  import rules from "@/validators/rules"
  import dayjs, { Dayjs } from "dayjs"
  import { IReviewPeriod } from "@/models/reviewPeriod/reviewPeriod.types"
  import UnderReviewPeriodModal from "./UnderReviewPeriodModal.vue"
  import { Status } from "@/types/Status"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { isEqual, cloneDeep } from "lodash-es"

  const initialFormData = () => ({
    reason: "",
    isBillingSuppressed: false,
    isDunningSuppressed: false,
    isPaymentCollectionSuppressed: false,
    from: null as Date | null,
    to: null as Date | null,
    description: "",
  })

  type ReviewPeriodTableItem = {
    id: number
    reason: string
    from: Dayjs
    to?: Dayjs
    suppressed: SoDataTableCustomComponent[]
    isActive: boolean
  }

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoStatus,
      SoButton,
      SoForm,
      SoSelect,
      SoDatePicker,
      SoCheckbox,
      SoDivider,
      SoDataTable,
      SoAlert,
      SoTextarea,
      UnderReviewPeriodModal,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
    },
    emits: ["update:modelValue"],
    data() {
      return {
        current: useCurrentAccountStore(),
        snackbar: useSnackbarStore(),
        addReviewPeriodMutator: useCurrentAccountStore().addReviewPeriod(),
        editReviewPeriodMutator: useCurrentAccountStore().editReviewPeriod(),
        addNoteMutator: useCurrentAccountStore().addNote(),
        submitError: null as null | string,
        showReviewPeriodForm: false,
        isEditForm: false,
        currentReviewPeriodId: undefined as undefined | number,
        currentReviewPeriodFormData: initialFormData(),
        openReviewPeriodModal: false,
        form: initialFormData(),
        headers: [
          { key: "reason", title: "Reason" },
          {
            key: "from",
            title: "From",
            type: SoDataTableColumnType.DATE,
          },
          {
            key: "to",
            title: "To",
            type: SoDataTableColumnType.DATE,
          },
          {
            key: "suppressed",
            title: "Suppressed",
            type: SoDataTableColumnType.STATUSES,
          },
        ],
        reviewPeriodReasons,
        rules,
        reasonRules: [rules.required],
        Status,
      }
    },
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
      loading() {
        return (
          this.current.underReviewPeriods.isFetching ||
          this.addReviewPeriodMutator.isPending
        )
      },
      reviewPeriodDataTableItems() {
        return this.current.underReviewPeriods.data.map((period) => ({
          id: period.id,
          reason: period.reason,
          from: period.fromDttm,
          to: period.toDttm,
          suppressed: this.getSuppressionStatuses(period),
          isActive: period.isActive,
        }))
      },
      activeReviewPeriodDataTableItems() {
        return this.reviewPeriodDataTableItems.filter(
          (period) => period.isActive,
        )
      },
      nonActiveReviewPeriodDataTableItems() {
        return this.reviewPeriodDataTableItems.filter(
          (period) => !period.isActive,
        )
      },
      toDateRules() {
        return [
          rules.required,
          () => {
            if (!this.form.to) return true
            if (dayjs(this.form.to).isAfter(dayjs().add(3, "month"))) {
              return "You cannot set a review period for more than 3 months"
            }
            return true
          },
          () => {
            if (!this.form.to || !this.form.from) return true
            if (
              dayjs(this.form.from).add(1, "day").isAfter(dayjs(this.form.to))
            ) {
              return "The review period must last at least one day"
            }
            return true
          },
        ]
      },
      editFormChanged() {
        return !isEqual(this.form, this.currentReviewPeriodFormData)
      },
    },
    watch: {
      showReviewPeriodForm(value) {
        if (!value) {
          this.resetForm()
        }
      },
      value(value) {
        if (!value) {
          this.showReviewPeriodForm = false
          this.resetForm()
        }
      },
    },
    methods: {
      handleSubmit() {
        this.submitError = null
        const reviewPeriodData = {
          reason: this.form.reason,
          isBillingSuppressed: this.form.isBillingSuppressed,
          isDunningSuppressed: this.form.isDunningSuppressed,
          isPaymentCollectionSuppressed:
            this.form.isPaymentCollectionSuppressed,
          fromDttm: dayjs(this.form.from),
          toDttm: dayjs(this.form.to),
          description: this.form.description,
        }

        if (this.isEditForm) {
          this.editReviewPeriodMutator
            .mutateAsync({
              ...reviewPeriodData,
              reviewPeriodJuniferId: this.currentReviewPeriodId,
            })
            .then(() =>
              this.addNote(
                "Review Edited",
                "Edited an account review period on",
              ),
            )
            .then(() => {
              this.snackbar.addSuccess(
                "Successfully edited Review Period. It may take some time for the changes to take effect",
              )
              this.resetForm()
              this.showReviewPeriodForm = false
            })
          return
        }

        this.addReviewPeriodMutator
          .mutateAsync({
            ...reviewPeriodData,
          })
          .then(() => {
            this.snackbar.addSuccess(
              "Successfully created Review Period. It may take some time for the changes to take effect",
            )
            this.resetForm()
            this.showReviewPeriodForm = false
          })
          .catch((error: unknown) => {
            // The mutator throws on an upstream Error union member. Catch it
            // here so the rejected promise doesn't escape into SoErrorBoundary
            // (which would show a generic "Failed to render content"); surface
            // the real gateway message in the in-modal error alert instead.
            this.submitError =
              error instanceof Error
                ? error.message
                : "Failed to create review period"
          })
      },
      /*
        The note is a side effect of an edit that has already succeeded. A note
        failure is surfaced by the addNoteMutator.isError alert, so it is
        reported but deliberately does not block the edit's success feedback.
      */
      addNote(topic: string, content: string) {
        const now = formatDateTime(dayjs())
        return this.addNoteMutator
          .mutateAsync({
            subject: "General Note",
            summary: topic,
            content: `${content} ${now}`,
          })
          .catch((error: unknown) => {
            errorReporter.report(error)
          })
      },
      getSuppressionStatuses(
        period: IReviewPeriod,
      ): SoDataTableCustomComponent<Status>[] {
        const statuses: string[] = []

        if (period.isDunningSuppressed) {
          statuses.push("Dunning")
        }

        if (period.isBillingSuppressed) {
          statuses.push("Billing")
        }

        if (period.isPaymentCollectionSuppressed) {
          statuses.push("Payment Collection")
        }

        if (period.isPropertySuppressed) {
          statuses.push("Property")
        }

        if (period.isPaymentReviewSuppressed) {
          statuses.push("Payment Review")
        }

        return statuses.map((text) => ({
          value: text,
          props: { status: new Status("neutral", text) },
        }))
      },
      resetForm() {
        this.form = initialFormData()
        this.isEditForm = false
        this.currentReviewPeriodId = undefined
        const form = this.$refs.form as typeof SoForm
        this.$nextTick(form.resetValidation)
      },
      openReviewPeriod(reviewPeriod: ReviewPeriodTableItem) {
        this.currentReviewPeriodId = reviewPeriod.id
        this.openReviewPeriodModal = true
      },
      showEditReviewPeriodForm(reviewPeriod: ReviewPeriodTableItem) {
        this.currentReviewPeriodId = reviewPeriod.id
        const reviewPeriodData = this.current.underReviewPeriods.data.find(
          (rp: IReviewPeriod) => rp.id === reviewPeriod.id,
        )
        if (reviewPeriodData) {
          this.showReviewPeriodForm = true
          this.isEditForm = true

          this.form = {
            reason: reviewPeriodData.reason,
            isBillingSuppressed: reviewPeriodData.isBillingSuppressed,
            isDunningSuppressed: reviewPeriodData.isDunningSuppressed,
            isPaymentCollectionSuppressed:
              reviewPeriodData.isPaymentCollectionSuppressed,
            from: reviewPeriodData.fromDttm.toDate(),
            to: reviewPeriodData.toDttm
              ? reviewPeriodData.toDttm.toDate()
              : null,
            description: reviewPeriodData.description
              ? reviewPeriodData.description
              : "",
          }
          this.currentReviewPeriodFormData = cloneDeep(this.form)
        }
      },
    },
  })
</script>
